import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { VideosLayout } from "@/components/videos/VideosLayout";
import { useVideosData } from "@/hooks/useVideosData";
import { saveToHistory } from "@/lib/videosApi";
import { Play, ExternalLink } from "lucide-react";

export const VideosWatchPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { videos, collections, loading, error, refetch } = useVideosData();
  const [isPlaying, setIsPlaying] = useState(false);

  const video = videos.find((v) => v.id === id);

  useEffect(() => {
    // Redirect to the first collection containing this video
    if (!loading && video) {
      const containingCollection = collections.find((c) =>
        c.lessons.includes(video.id),
      );
      if (containingCollection) {
        navigate(`/videos/course/${containingCollection.slug}/${video.id}`, {
          replace: true,
        });
        return;
      }
    }
  }, [loading, video, collections, navigate]);

  const handlePlay = () => {
    setIsPlaying(true);
    if (video) {
      saveToHistory({
        id: video.id,
        t: video.title,
        href: `/videos/watch/${video.id}`,
      });
    }
  };

  if (loading) {
    return (
      <VideosLayout loading={true}>
        <div />
      </VideosLayout>
    );
  }

  if (!video) {
    return (
      <VideosLayout error={error} onRetry={refetch}>
        <SEOHead
          title="Video Not Found | JNN Homes Northern Virginia"
          description="The requested video could not be found."
          noIndex={true}
        />
        <div className="p-12 text-center rounded-2xl bg-slate-50 border border-slate-200">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Video not found
          </h1>
          <Link
            to="/videos"
            className="text-sm font-semibold text-[#9A5207] hover:underline"
          >
            Back to all videos
          </Link>
        </div>
      </VideosLayout>
    );
  }

  return (
    <VideosLayout loading={loading} error={error} onRetry={refetch}>
      <SEOHead
        title={`${video.title} | JNN Homes Northern Virginia`}
        description={`Watch: ${video.title} — vacation rental management video from JNN Homes.`}
        noIndex={true}
      />
      <div className="space-y-6">
        <nav className="flex items-center gap-2 text-xs text-slate-500 font-medium">
          <Link to="/videos" className="hover:text-[#9A5207]">
            Videos
          </Link>
          <span>›</span>
          <span className="text-slate-900 font-semibold truncate">
            {video.title}
          </span>
        </nav>

        <div
          className="relative w-full overflow-hidden rounded-2xl bg-slate-900"
          style={{ maxHeight: "640px" }}
        >
          {isPlaying ? (
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0&playsinline=1`}
              title={video.title}
              className="w-full border-0"
              style={{ height: "min(74vh, 640px)", aspectRatio: "16 / 9" }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div
              className="relative w-full bg-slate-200 cursor-pointer group"
              style={{ height: "640px", maxHeight: "74vh" }}
              onClick={handlePlay}
            >
              <img
                src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
                alt={`Thumbnail for ${video.title}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-slate-900/40 flex items-center justify-center">
                <button
                  onClick={handlePlay}
                  aria-label={`Play video: ${video.title}`}
                  className="w-16 h-16 rounded-full bg-[#E8850F] text-white flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                >
                  <Play className="w-7 h-7 fill-white ml-1" />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <h1 className="text-xl font-extrabold text-slate-900">
            {video.title}
          </h1>
          <div className="flex items-center gap-3 text-xs text-slate-500 font-medium">
            {video.date && (
              <time dateTime={video.date}>
                {new Date(video.date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </time>
            )}
            <span>·</span>
            <span>{video.duration}</span>
            <span>·</span>
            <span className="capitalize">{video.category}</span>
          </div>
          <a
            href={`https://www.youtube.com/watch?v=${video.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#9A5207] hover:underline"
          >
            Watch on YouTube <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </VideosLayout>
  );
};
